import { useEffect, useState, useCallback, useMemo } from "react";
import { Common, CustomScrollbar, Icon } from "@/components";
import { useImportOrder } from "../providers/ImportOrderContext";
import { useNavigate } from "react-router-dom";
import { Address, FixedAddress, GoogleAddressPrediction } from "../models";
import LocateAddressDialog from "@/components/Googlemap/LocateAddressDialog";
import {
  GOOGLE_API_KEY,
  GOOGLE_MAP_OPTIONS,
} from "@/configuration/globalVariable";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import AddressTable, { AddressWithStatus } from "./AddressTable";
import { useFixAddresses } from "../services/salesOrdersService";
import { SnackbarUtils } from "@/utils/notistack";
import {
  combineAddressComponents,
  extractAddressComponents,
  GoogleGeocodingResult,
  isEmptyAddress,
  isMissingAddressComponents,
  LocationWithComponents,
} from "@/utils/address";
import { useAppSelector } from "@/sharedStore/hooks";
import { storageAddressSelector } from "../../Delivery/providers/deliveryManifest/selector";
import { useGooglePlaceAutocomplete } from "../hooks/useGooglePlaceAutocomplete";

// Loading state renderer for the wrapper
const renderStatus = (status: Status) => {
  switch (status) {
    case Status.LOADING:
      return (
        <div className="w-full h-full flex items-center justify-center">
          Map is loading.....
        </div>
      );
    case Status.FAILURE:
      return (
        <div className="w-full h-full flex items-center justify-center">
          Map loaded error.
        </div>
      );
    default:
      return (
        <div className="w-full h-full flex items-center justify-center">
          Loading.....
        </div>
      );
  }
};

const FixAddresses = () => {
  const { state, dispatch } = useImportOrder();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState<AddressWithStatus[]>([]);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [currentAddress, setCurrentAddress] =
    useState<AddressWithStatus | null>(null);
  const [showSuggestions, setShowSuggestions] =
    useState<AddressWithStatus | null>(null);
  const [pendingSuggestions, setPendingSuggestions] = useState(0);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const fixAddressesMutation = useFixAddresses();
  const storageAddress = useAppSelector(storageAddressSelector);
  const {
    suggestions,
    loading: isLoadingSuggestions,
    fetchSuggestions,
  } = useGooglePlaceAutocomplete(storageAddress?.country);

  const currentLocation: LocationWithComponents = useMemo(() => {
    return {
      lat: currentAddress?.fixedAddress?.latitude,
      lng: currentAddress?.fixedAddress?.longitude,
      address: currentAddress?.fixedAddress?.fullAddress,
      components: {
        addressLine1: currentAddress?.fixedAddress?.addressLine1,
        addressLine2: currentAddress?.fixedAddress?.addressLine2,
        suburb: currentAddress?.fixedAddress?.suburb,
        state: currentAddress?.fixedAddress?.state,
        postcode: currentAddress?.fixedAddress?.postcode,
        country: currentAddress?.fixedAddress?.country,
      },
    } as LocationWithComponents;
  }, [currentAddress]);

  const hasMissingAddressComponents = useMemo(() => {
    return addresses.some(
      (address) =>
        address.fixedAddress && isMissingAddressComponents(address.fixedAddress)
    );
  }, [addresses]);

  const hasEmptyAddresses = useMemo(() => {
    return addresses.some(
      (address) =>
        !address.isFixed &&
        isEmptyAddress(address.fixedAddress) &&
        !address.fixedAddress?.fullAddress
    );
  }, [addresses]);

  // Set flag when Google Maps API is loaded
  const handleApiLoad = useCallback(() => {
    setIsGoogleMapsLoaded(true);
  }, []);

  // Initialize addresses when component mounts or invalidAddresses changes
  useEffect(() => {
    if (state.invalidAddresses && state.invalidAddresses.length > 0) {
      // Convert invalid addresses to our extended format with status
      const addressesWithStatus: AddressWithStatus[] =
        state.invalidAddresses.map((invalidAddress) => {
          return {
            ...invalidAddress,
            fixedAddress: {
              addressLine1: invalidAddress.preferredAddress?.addressLine1 ?? "",
              addressLine2: invalidAddress.preferredAddress?.addressLine2 ?? "",
              suburb: invalidAddress.preferredAddress?.suburb ?? "",
              state: invalidAddress.preferredAddress?.state ?? "",
              postcode: invalidAddress.preferredAddress?.postcode ?? "",
              country: invalidAddress.preferredAddress?.country ?? "",
              latitude: invalidAddress.preferredAddress?.latitude ?? 0,
              longitude: invalidAddress.preferredAddress?.longitude ?? 0,
              fullAddress: invalidAddress.preferredAddress?.fullAddress ?? "",
            },
            isFixed: false,
            hasSuggestion: !!invalidAddress.preferredAddress,
            isGeocoding: true,
          };
        });

      setAddresses(addressesWithStatus);

      // Get suggestions only for addresses that aren't already fixed
      if (isGoogleMapsLoaded) {
        addressesWithStatus
          .filter((address) => !address.isFixed)
          .forEach((address) => {
            getAddressSuggestion(address);
          });
      }
    }
  }, [state.invalidAddresses, isGoogleMapsLoaded]);

  // Count pending suggestions after addresses change and update initial approval count
  useEffect(() => {
    // Update pending suggestions count
    const pendingCount = addresses.filter(
      (addr) =>
        addr.hasSuggestion &&
        !addr.isFixed &&
        !isEmptyAddress(addr.fixedAddress)
    ).length;
    setPendingSuggestions(pendingCount);
  }, [addresses]);

  // Get suggestions for an address using Google Maps Geocoding API
  const getAddressSuggestion = async (address: AddressWithStatus) => {
    let fixedAddress: Address | undefined;

    try {
      if (!address.address) {
        return;
      }

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          address.address
        )}&key=${GOOGLE_API_KEY}`
      );
      const data = await response.json();

      if (data.status === "OK" && data.results && data.results.length > 0) {
        const result = data.results[0] as GoogleGeocodingResult;

        const addressComponents = extractAddressComponents(
          result?.address_components ?? []
        );

        if (!isMissingAddressComponents(addressComponents)) {
          fixedAddress = {
            addressLine1: addressComponents.addressLine1 || "",
            addressLine2: addressComponents.addressLine2 || "",
            suburb: addressComponents.suburb || "",
            state: addressComponents.state || "",
            postcode: addressComponents.postcode || "",
            country: addressComponents.country || "",
            latitude: result.geometry.location.lat,
            longitude: result.geometry.location.lng,
            fullAddress: combineAddressComponents(addressComponents),
          };
        }
      }
    } catch (error) {
      console.error("Error getting address suggestion:", error);
    } finally {
      if (fixedAddress) {
        setAddresses((prev) =>
          prev.map((addr) =>
            addr.customerName === address.customerName &&
            addr.address === address.address
              ? {
                  ...addr,
                  fixedAddress,
                  hasSuggestion: true,
                  isGeocoding: false,
                }
              : addr
          )
        );
      } else {
        setAddresses((prev) =>
          prev.map((addr) =>
            addr.customerName === address.customerName &&
            addr.address === address.address
              ? { ...addr, isGeocoding: false }
              : addr
          )
        );
      }
    }
  };

  // Handle address input change
  const handleAddressChange = (
    changingAddress: AddressWithStatus,
    value: string
  ) => {
    // Update the address value
    setAddresses((prev) =>
      prev.map((addr) =>
        addr.customerName === changingAddress.customerName &&
        addr.address === changingAddress.address
          ? {
              ...addr,
              fixedAddress: {
                addressLine1: "",
                addressLine2: "",
                suburb: "",
                state: "",
                postcode: "",
                country: "",
                latitude: 0,
                longitude: 0,
                fullAddress: value,
              },
              isFixed: false,
            }
          : addr
      )
    );

    // Show suggestions for this address
    setShowSuggestions(changingAddress);

    // Get new suggestions if Google Maps is loaded
    if (isGoogleMapsLoaded) {
      fetchSuggestions(value);
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = async (
    address: AddressWithStatus,
    prediction: GoogleAddressPrediction
  ) => {
    let fixedAddress: Address | undefined;

    try {
      if (!isGoogleMapsLoaded) return;
      // Hide suggestions
      setShowSuggestions(null);

      setAddresses((prev) =>
        prev.map((addr) =>
          addr.customerName === address.customerName &&
          addr.address === address.address
            ? { ...addr, isGeocoding: true }
            : addr
        )
      );

      // Get detailed information about the selected place
      const geocoder = new google.maps.Geocoder();
      const { results } = await new Promise<google.maps.GeocoderResponse>(
        (resolve, reject) => {
          geocoder.geocode(
            { placeId: prediction.place_id },
            (results, status) => {
              if (status === "OK") {
                resolve({ results: results || [] });
              } else {
                reject(new Error(`Geocode failed with status: ${status}`));
              }
            }
          );
        }
      );

      if (results && results.length > 0) {
        const location = results[0].geometry.location;

        // Extract address components
        const addressComponents = extractAddressComponents(
          results[0].address_components
        );

        fixedAddress = {
          addressLine1: addressComponents.addressLine1 || "",
          addressLine2: addressComponents.addressLine2 || "",
          suburb: addressComponents.suburb || "",
          state: addressComponents.state || "",
          postcode: addressComponents.postcode || "",
          country: addressComponents.country || "",
          latitude: location.lat(),
          longitude: location.lng(),
          fullAddress: combineAddressComponents(addressComponents),
        };
      }
    } catch (error) {
      console.error("Error handling suggestion:", error);
    } finally {
      if (fixedAddress) {
        setAddresses((prev) =>
          prev.map((addr) =>
            addr.customerName === address.customerName &&
            addr.address === address.address
              ? {
                  ...addr,
                  fixedAddress,
                  isFixed: true,
                  hasSuggestion: true,
                  isGeocoding: false,
                }
              : addr
          )
        );
      }
    }
  };

  // Approve a fixed address
  const handleApproveAddress = (address: AddressWithStatus) => {
    if (isEmptyAddress(address.fixedAddress)) {
      SnackbarUtils.error("Please review and fill the correct address");
      return;
    }

    setAddresses((prev) =>
      prev.map((addr) =>
        addr.customerName === address.customerName &&
        addr.address === address.address
          ? { ...addr, isFixed: true }
          : addr
      )
    );
  };

  // Open map dialog for an address
  const handleOpenMap = (address: AddressWithStatus) => {
    setCurrentAddress(address);
    setShowSuggestions(null);
    setMapDialogOpen(true);
  };

  // Handle map location confirmation
  const handleMapLocationConfirm = (location: LocationWithComponents) => {
    if (currentAddress) {
      if (!location.lat || !location.lng) {
        SnackbarUtils.error("We could not locate the address on the map");
        return;
      }

      setAddresses((prev) =>
        prev.map((addr) =>
          addr.customerName === currentAddress.customerName &&
          addr.address === currentAddress.address
            ? {
                ...addr,
                fixedAddress: {
                  addressLine1: location.components?.addressLine1 ?? "",
                  addressLine2: location.components?.addressLine2 ?? "",
                  suburb: location.components?.suburb ?? "",
                  state: location.components?.state ?? "",
                  postcode: location.components?.postcode ?? "",
                  country: location.components?.country ?? "",
                  latitude: location.lat ?? 0,
                  longitude: location.lng ?? 0,
                  fullAddress: location.address ?? "",
                },
                isFixed: true,
              }
            : addr
        )
      );
      setMapDialogOpen(false);
      setCurrentAddress(null);
    }
  };

  // Approve all suggested addresses
  const handleApproveAllSuggested = () => {
    setAddresses((prev) =>
      prev.map((addr) =>
        addr.hasSuggestion &&
        !addr.isFixed &&
        !isEmptyAddress(addr.fixedAddress)
          ? { ...addr, isFixed: true }
          : addr
      )
    );
  };

  // Final confirmation to save fixed addresses
  const handleConfirm = async () => {
    // Convert addresses to the format expected by the API
    const fixedAddresses: FixedAddress[] = addresses
      .filter((addr) => addr.isFixed && addr.fixedAddress)
      .map((addr) => ({
        customerName: addr.customerName,
        invalidAddress: addr.address,
        fixedAddress: addr.fixedAddress!,
      }));

    const fixAddressesResult = await fixAddressesMutation.mutateAsync({
      importSessionId: state.importSessionId ?? "",
      fixedAddresses,
    });

    if (!fixAddressesResult.success || fixAddressesResult.errors.length > 0) {
      fixAddressesResult.errors.forEach((error) => {
        SnackbarUtils.error(error);
      });
      return;
    }

    const invalidRoutesDocNumbers = Array.from(
      new Set(
        state.invalidRoutes
          .map((invalidRoute) => invalidRoute.docNumbers)
          .flat()
      )
    );

    dispatch({
      type: "SET_INVALID_ORDER_COUNT",
      payload: invalidRoutesDocNumbers.length,
    });

    dispatch({
      type: "SET_INVALID_ADDRESSES",
      payload: [],
    });

    navigate("#review");
  };

  // Check if all addresses are fixed
  const allAddressesFixed =
    addresses.length > 0 && addresses.every((addr) => addr.isFixed);

  return GOOGLE_API_KEY ? (
    <>
      <Wrapper
        render={renderStatus}
        callback={handleApiLoad}
        {...GOOGLE_MAP_OPTIONS}
      >
        <div className="relative">
          <div className="h-[calc(100vh-258px)]">
            {hasEmptyAddresses ? (
              <div className="pl-0 flex items-center mb-4">
                <Icon.WarningCircle className="fill-red" size="16" />
                <span className="ml-2 text-neutral-50">
                  Some addresses are empty. Please review and fill the correct
                  address.
                </span>
              </div>
            ) : hasMissingAddressComponents ? (
              <div className="pl-0 flex items-center mb-4">
                <Icon.WarningCircle className="fill-yellow" size="16" />
                <span className="ml-2 text-neutral-50">
                  Some addresses may not be specific enough for delivery. Please
                  review and add missing details if possible.
                </span>
              </div>
            ) : null}
            <div className="h-[calc(100%-80px)]">
              <CustomScrollbar>
                <AddressTable
                  addresses={addresses}
                  showSuggestions={showSuggestions}
                  addressSuggestions={suggestions}
                  isLoadingSuggestions={isLoadingSuggestions}
                  handleAddressChange={handleAddressChange}
                  handleSelectSuggestion={handleSelectSuggestion}
                  handleApproveAddress={handleApproveAddress}
                  handleOpenMap={handleOpenMap}
                />
              </CustomScrollbar>
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-5">
            <Common.Button
              color="transparent"
              onClick={() => navigate("#review")}
            >
              Cancel
            </Common.Button>
            {pendingSuggestions > 0 && (
              <Common.Button
                onClick={handleApproveAllSuggested}
                color="blue"
                iconLeft={Icon.Checked}
              >
                Approve all suggested addresses ({pendingSuggestions})
              </Common.Button>
            )}
            <Common.Button
              onClick={handleConfirm}
              disabled={!allAddressesFixed || fixAddressesMutation.isLoading}
              isLoading={fixAddressesMutation.isLoading}
            >
              {fixAddressesMutation.isLoading ? "Fixing..." : "Confirm"}
            </Common.Button>
          </div>
        </div>
      </Wrapper>
      <LocateAddressDialog
        open={mapDialogOpen}
        onClose={() => setMapDialogOpen(false)}
        title="Select address on the map"
        currentAddress={currentAddress?.address || ""}
        initialLocation={currentLocation}
        onConfirm={handleMapLocationConfirm}
      />
    </>
  ) : null;
};

export default FixAddresses;
